import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMyJobs, getJobStats } from "../../../data/requests/job-requests";
import { AppLayout } from "../shared/layout";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table";
import { Eye, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";

export function JobListScreen() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  
  const { data: statsData } = useQuery({
    queryKey: ["job-stats"],
    queryFn: () => getJobStats().then(res => res.data),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["my-jobs", { status }],
    queryFn: () => getMyJobs({ status }).then(res => res.data),
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { variant: "secondary", icon: Clock, label: "Pending" },
      PROCESSING: { variant: "default", icon: AlertCircle, label: "Processing" },
      COMPLETED: { variant: "success", icon: CheckCircle, label: "Completed" },
      FAILED: { variant: "destructive", icon: XCircle, label: "Failed" },
      CANCELLED: { variant: "secondary", icon: XCircle, label: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="mr-1 h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const columns = [
    {
      key: "id",
      label: "Job ID",
    },
    {
      key: "template",
      label: "Template",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.template?.thumbnailUrl && (
            <img
              src={row.template.thumbnailUrl}
              alt={row.template.name}
              className="w-10 h-10 object-cover rounded"
            />
          )}
          <span>{row.template?.name}</span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      key: "processingTime",
      label: "Time",
      render: (row) => row.processingTime ? `${(row.processingTime / 1000).toFixed(2)}s` : "—",
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/app/jobs/${row.id}`)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Jobs</h1>
          <p className="text-gray-500 mt-2">
            View and manage your AI photo processing jobs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{statsData?.total || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{statsData?.pending || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Processing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{statsData?.processing || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{statsData?.completed || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Failed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">{statsData?.failed || 0}</p>
            </CardContent>
          </Card>
        </div>

        <DataTable
          data={data?.jobs || []}
          columns={columns}
          isLoading={isLoading}
        />
      </div>
    </AppLayout>
  );
}

